import { memo, useCallback, useMemo } from "react";

import { STATIC_CONTENT_BLOCK_TYPES, getBlockIndexId } from "../../../shared/worksheets/constants";
import { ErrorBoundary } from "../../../worksheets/views/ErrorBoundary";

import { t } from "i18n/i18n";
import { TabBar } from "mds/components/TabBar";
import { cn } from "mds/utils/cn";
import { useAppSelector } from "store/index";
import { selectCurrentAvailablePageBlocks, selectSelectedPageBlockIndex } from "store/selectors";

export const PageTableOfContents = memo(
  () => {
    const closestPageBlockIndex = useAppSelector((s) => selectSelectedPageBlockIndex(s));
    const currentAvailablePageBlockIds = useAppSelector((s) => selectCurrentAvailablePageBlocks(s));

    const selectedPageBlockIndex = useMemo(() => {
      // We transform the index to find the first non-static block, since those aren't listed
      // in the table of contents currently.
      let index = closestPageBlockIndex ?? 0;
      while (
        index > 0 &&
        STATIC_CONTENT_BLOCK_TYPES.includes(currentAvailablePageBlockIds[index].blockType)
      ) {
        index -= 1;
      }
      return index;
    }, [closestPageBlockIndex, currentAvailablePageBlockIds]);

    const filteredBlocks = useMemo(() => {
      return currentAvailablePageBlockIds.filter(
        ({ blockType }) => !STATIC_CONTENT_BLOCK_TYPES.includes(blockType),
      );
    }, [currentAvailablePageBlockIds]);

    const tabs = useMemo(
      () =>
        filteredBlocks.map(({ index, blockType }, i) => {
          const defaultValue = t(`fields.headers.${blockType}`, { defaultValue: blockType });
          const label = t(`fields.header_overrides.${blockType}`, { defaultValue });
          return {
            key: getBlockIndexId(blockType, index),
            label: <div className="flex cursor-pointer items-center">{label}</div>,
            title: label,
            eventName: "Sidebar - Scroll to block",
            eventProperties: { blockType, index: i + 1 },
            disabled: false,
          };
        }),
      [filteredBlocks],
    );

    const onTabChange = useCallback(
      (index: number) => {
        const tab = tabs[index];
        document.getElementById(tab.key)?.scrollIntoView({
          behavior: "smooth",
        });
      },
      [tabs],
    );

    if (!filteredBlocks.length) {
      return null;
    }

    return (
      <ErrorBoundary>
        <TabBar
          className={cn("mx-3 my-2 flex w-full items-center")}
          kind="vertical"
          selectedIndex={selectedPageBlockIndex}
          tabs={tabs}
          skipBackgroundPanelRender
          onChange={onTabChange}
        />
      </ErrorBoundary>
    );
  },
  () => false,
);

PageTableOfContents.displayName = "PageTableOfContents";
